import Card from 'react-bootstrap/Card';
import Placeholder from 'react-bootstrap/Placeholder';
import styled from 'styled-components';

import {todoCircleDiameter, todoConnectorWidth} from '../constants';

const skeletonGrey = '#e5e5e5'; // Placeholder grey color
const thumbnailGrey = '#f5f5f5'; // Placeholder grey for thumbnails
const treasureMapCircleDiameter = 24; // Diameter for the treasure map circles

/* ────────────────────────────────────────────────────────── */
/* Shared layout constants (identical to TodoConnector)      */
const GAP_X = 10;
const GAP_Y = 10;

// I want the border to be a very light grey
const ModuleCardWrapper = styled(Card)`
  scroll-snap-align: start;
  position: relative;
  margin: 1rem 0; /* replaces .mb-3 .mt-3 utilities      */
  border: 1px solid ${skeletonGrey};
`;

//  I want to set the background color to a light grey
const ThumbnailSkeleton = styled(Placeholder)`
  height: 160px;
  border-radius: 0.25rem 0.25rem 0 0;
  background-color: ${thumbnailGrey};
`;

const TitleBar = styled.div`
  position: absolute;
  top: 100px;
  left: 0.75rem;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  padding: 4px 8px;
  width: 120px;
  height: 40px;
`;

const OverviewBtnSkeleton = styled(Placeholder.Button)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 90px;
  height: 38px;
`;

const TodoRow = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  margin-top: 10px;
  z-index: 2;
`;

const TodoCircleSkeleton = styled(Placeholder).attrs({animation: 'wave'})`
  && {
    width: ${todoCircleDiameter}px;
    height: ${todoCircleDiameter}px;
    background: ${skeletonGrey};
    border-radius: 50%;
  }
`;

const TodoLabelSkeleton = styled(Placeholder)`
  flex-grow: 1;
  margin-left: 10px;
  background-color: ${skeletonGrey};
  border-radius: 4px;
  max-width: 300px;
`;

/* Vertical bar segments (exactly like <TodoConnector>) */
const ConnectorSegment = styled.div`
  position: absolute;
  width: ${todoConnectorWidth}px;
  background: ${skeletonGrey};
  z-index: 1;
`;

// has ~5 circles which are centered with a small gap
const TreasureMapOuter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 1rem;
`;

const TreasureMapCircle = styled.div`
  width: ${treasureMapCircleDiameter}px;
  height: ${treasureMapCircleDiameter}px;
  border-radius: 50%;
  background-color: ${skeletonGrey};
`;

const TreasureMapSkeleton = ({nCircles = 6}) => {
  return (
    <TreasureMapOuter>
      {Array.from({length: nCircles}).map((_, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        <TreasureMapCircle key={i} />
      ))}
    </TreasureMapOuter>
  );
};

/* Draws the connector for one module */
const SkeletonConnector = ({todoCount}) => {
  const radius = todoCircleDiameter / 2;
  const left = radius + GAP_X - todoConnectorWidth / 2;
  const top = radius + GAP_Y;
  const segmentHeight = todoCircleDiameter + GAP_Y;

  return (
    <>
      {Array.from({length: todoCount - 1}).map((_, i) => (
        <ConnectorSegment
          // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          key={i}
          style={{
            height: segmentHeight,
            marginLeft: left,
            marginTop: top + i * segmentHeight,
          }}
        />
      ))}
    </>
  );
};

/* ────────────────────────────────────────────────────────── */
/* Single Module Card Skeleton (reusable)                    */
/**
 * Placeholder card that mirrors the shape of a real ModuleCard, used while
 * module data is being fetched.
 */
export const ModuleCardSkeleton = ({todosPerModule = 8}) => (
  <ModuleCardWrapper aria-hidden="true">
    <ThumbnailSkeleton animation="wave" />

    <TitleBar />

    <OverviewBtnSkeleton variant="light" />

    <Card.Body style={{position: 'relative'}}>
      <SkeletonConnector todoCount={todosPerModule} />

      {Array.from({length: todosPerModule}).map((_, todoIdx) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        <TodoRow key={todoIdx}>
          <TodoCircleSkeleton />
          <TodoLabelSkeleton xs={5} />
        </TodoRow>
      ))}
    </Card.Body>
  </ModuleCardWrapper>
);

/* ────────────────────────────────────────────────────────── */
/* Main Skeleton Component                                   */
/**
 * Full roadmap loading skeleton: a treasure-map row plus N module-card
 * placeholders.
 */
export const RoadmapSkeleton = ({modules = 2, todosPerModule = 8}) => (
  <>
    <TreasureMapSkeleton />
    {Array.from({length: modules}).map((_, moduleIdx) => (
      <ModuleCardSkeleton
        // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        key={moduleIdx}
        todosPerModule={todosPerModule}
      />
    ))}
  </>
);
